/**
 * Scrollable message thread.
 *
 * Responsibilities:
 *  • one scroll container per thread (no nested scrollbars, no body scroll)
 *  • date separators + author grouping via buildThreadEntries()
 *  • progressive rendering: only the newest page is mounted, older pages load
 *    on demand — long threads no longer freeze the main thread
 *  • scroll anchoring: stays pinned to the newest message, keeps the reading
 *    position when history is prepended, offers a jump-to-latest pill
 *  • accessible: role="log" + aria-live so screen readers announce new messages
 */

import React from 'react';
import { ChevronUp, Loader2 } from 'lucide-react';
import type { MessageAttachment, MessagingMessage } from './types';
import { MessageBubble } from './MessageBubble';
import {
  DateSeparator,
  JumpToLatest,
  MessageThreadSkeleton,
  TypingIndicator,
  UnreadSeparator,
} from './primitives';
import { useAutoScroll, useMessageWindow } from './hooks';
import { buildThreadEntries, toTime } from './utils';

export interface MessageThreadProps {
  messages: MessagingMessage[];
  /** Identity of the open conversation — resets scroll + paging when it changes. */
  conversationKey: string;
  isOwnMessage: (message: MessagingMessage) => boolean;
  loading?: boolean;
  /** Rendered when there are no messages and we are not loading. */
  emptyState?: React.ReactNode;
  /** Rendered instead of messages when loading failed. */
  errorState?: React.ReactNode;
  typingNames?: string[];
  showAuthor?: boolean;
  showAvatars?: boolean;
  showReceipts?: boolean;
  highlight?: string;
  firstUnreadIndex?: number | null;
  unreadCount?: number;
  pageSize?: number;
  onReply?: (message: MessagingMessage) => void;
  onMore?: (message: MessagingMessage, anchor: HTMLElement) => void;
  /** Toggle a reaction (ticket threads); bubbles render chips + picker when set. */
  onReact?: (message: MessagingMessage, reaction: string) => void;
  /** Viewer id used to highlight the viewer's own reactions. */
  reactionViewerId?: string;
  onPreviewImage?: (attachment: MessageAttachment) => void;
  /** Optional hook so features can fetch an older page from the backend. */
  onLoadOlder?: () => void;
  ariaLabel?: string;
  header?: React.ReactNode;
}

export function MessageThread({
  messages,
  conversationKey,
  isOwnMessage,
  loading = false,
  emptyState,
  errorState,
  typingNames,
  showAuthor = true,
  showAvatars = true,
  showReceipts = true,
  highlight,
  firstUnreadIndex = null,
  unreadCount = 0,
  pageSize = 40,
  onReply,
  onMore,
  onReact,
  reactionViewerId,
  onPreviewImage,
  onLoadOlder,
  ariaLabel = 'Conversation messages',
  header,
}: MessageThreadProps) {
  const {
    visible,
    hasMore,
    hiddenCount,
    loadMore,
  } = useMessageWindow(messages, conversationKey, pageSize);

  const {
    scrollRef,
    bottomRef,
    hasNewBelow,
    isAtBottom,
    scrollToBottom,
    onScroll,
    captureScrollAnchor,
  } = useAutoScroll(visible.length, conversationKey);

  const latestIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    latestIdRef.current = messages.length ? messages[messages.length - 1].id : null;
  }, [messages]);

  /* Screen-reader announcements (Chat Phase 4). The scroll container
     used to be a polite live region, which announced EVERYTHING the
     log rendered: prepended history pages, search filtering, edits —
     pure spam. Announcements now come
     from this dedicated region and fire only for a genuinely new
     incoming message appended at the tail: never on initial load or a
     conversation switch, never for the viewer's own (or optimistic)
     messages, never for prepends/replacements, and never for a stale
     tail restored by clearing a search (recency guard). The body text
     is never read aloud — only the arrival and its author. */
  const [announcement, setAnnouncement] = React.useState<{ seq: number; text: string } | null>(null);
  const tailRef = React.useRef<{ key: string; lastId: string | null; count: number } | null>(null);
  React.useEffect(() => {
    const last = messages.length ? messages[messages.length - 1] : null;
    const prev = tailRef.current;
    tailRef.current = { key: conversationKey, lastId: last?.id ?? null, count: messages.length };
    if (!prev || prev.key !== conversationKey || !last) return;
    if (prev.lastId === last.id || messages.length <= prev.count) return;
    if (last.delivery === 'sending' || last.delivery === 'failed') return;
    if (isOwnMessage(last)) return;
    if (Date.now() - toTime(last.createdAt) > 30_000) return;
    const who = last.authorName || 'Someone';
    setAnnouncement((current) => ({
      seq: (current?.seq || 0) + 1,
      text: last.isInternal ? `New internal note from ${who}` : `New message from ${who}`,
    }));
  }, [messages, conversationKey, isOwnMessage]);

  const entries = React.useMemo(() => {
    const offset = messages.length - visible.length;
    const windowUnreadIndex =
      firstUnreadIndex == null ? null : firstUnreadIndex - offset >= 0 ? firstUnreadIndex - offset : null;
    return buildThreadEntries(visible, {
      isOwnMessage,
      firstUnreadIndex: windowUnreadIndex,
      unreadCount,
    });
  }, [visible, messages.length, isOwnMessage, firstUnreadIndex, unreadCount]);

  const handleLoadOlder = React.useCallback(() => {
    captureScrollAnchor();
    if (hasMore) loadMore();
    else onLoadOlder?.();
  }, [captureScrollAnchor, hasMore, loadMore, onLoadOlder]);

  // Auto-page when the user scrolls to the very top of the rendered window.
  const handleScroll = React.useCallback(() => {
    onScroll();
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 48 && (hasMore || onLoadOlder)) handleLoadOlder();
  }, [onScroll, scrollRef, hasMore, onLoadOlder, handleLoadOlder]);

  // Keep the sticky header mounted in the error state so the user can still
  // navigate back, search or retry instead of being trapped on a blank pane.
  if (errorState) {
    return (
      <>
        {header}
        <div className="msg-scroll" ref={scrollRef}>
          {errorState}
        </div>
      </>
    );
  }

  const showEmpty = !loading && messages.length === 0;

  return (
    <>
      {header}
      <div
        ref={scrollRef}
        className="msg-scroll"
        onScroll={handleScroll}
        role="log"
        aria-live="off"
        aria-label={ariaLabel}
        tabIndex={0}
      >
        {loading && messages.length === 0 && <MessageThreadSkeleton />}

        {showEmpty && emptyState}

        {!showEmpty && (hasMore || onLoadOlder) && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 10px' }}>
            <button type="button" className="msg-chip" onClick={handleLoadOlder}>
              {loading ? <Loader2 size={13} className="msg-spin" /> : <ChevronUp size={13} />}
              {hiddenCount > 0 ? `Load ${Math.min(hiddenCount, pageSize)} earlier messages` : 'Load earlier messages'}
            </button>
          </div>
        )}

        {/* Pushes short threads to the bottom so the first message sits above
            the composer instead of floating in the middle of the pane. */}
        {!showEmpty && <div className="msg-scroll-spacer" aria-hidden="true" />}

        {entries.map((entry) => {
          if (entry.kind === 'date') return <DateSeparator key={entry.key} date={entry.date} />;
          if (entry.kind === 'unread') return <UnreadSeparator key={entry.key} count={entry.count} />;
          return (
            <MessageBubble
              key={entry.key}
              message={entry.message}
              isOwn={entry.isOwn}
              grouped={entry.grouped}
              lastOfGroup={entry.lastOfGroup}
              showAuthor={showAuthor}
              showAvatar={showAvatars}
              showReceipts={showReceipts}
              highlight={highlight}
              onReply={onReply}
              onMore={onMore}
              onReact={onReact}
              reactionViewerId={reactionViewerId}
              onPreviewImage={onPreviewImage}
              animate={entry.message.id === latestIdRef.current && isAtBottom}
            />
          );
        })}

        {typingNames && typingNames.length > 0 && <TypingIndicator names={typingNames} />}

        <div ref={bottomRef} style={{ height: 1, flexShrink: 0 }} aria-hidden="true" />

        {hasNewBelow && <JumpToLatest onClick={() => scrollToBottom(true)} label="New messages" />}
      </div>

      {announcement && (
        <div
          key={announcement.seq}
          className="msg-visually-hidden"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement.text}
        </div>
      )}
    </>
  );
}
